import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import { getMetricTypeInfo } from "@/metrics/metric-display"
import type { MetricAutoAddRuleSource } from "@/metrics/runtime-adapters"

import type { RunRowLike } from "@/types/RunRow"

const DEFAULT_NUMERIC_FILTER_VALUE = "0"

const BINARY_FILTER_VALUES = [
  ANNOTATION_VALUE.YES,
  ANNOTATION_VALUE.NO,
  ANNOTATION_VALUE.UNKNOWN,
]

export const getMetricFilterInfo = (
  metric: { id: string } | undefined,
  runRows: RunRowLike[],
): { type: "string" | "number"; uniqueValues: string[] } => {
  if (!metric) {
    return { type: "number", uniqueValues: [] }
  }

  const stringValues: string[] = []
  let hasStringValues = false

  runRows.forEach((row) => {
    const metricValue = row.metric_outputs?.[metric.id]
    if (metricValue !== undefined && metricValue !== null) {
      if (typeof metricValue === "object") {
        if (metricValue.output_type === "string" && metricValue.string_value) {
          stringValues.push(metricValue.string_value)
          hasStringValues = true
        }
      } else if (typeof metricValue === "string") {
        stringValues.push(metricValue)
        hasStringValues = true
      }
    }
  })

  return {
    type: hasStringValues ? "string" : "number",
    uniqueValues: hasStringValues
      ? Array.from(new Set(stringValues)).sort()
      : [],
  }
}

export const getDefaultFilterValue = (info: {
  type: "string" | "number"
  uniqueValues: string[]
}): string => {
  if (info.type === "string") {
    return info.uniqueValues.includes("YES")
      ? "YES"
      : (info.uniqueValues[0] ?? "")
  }
  return DEFAULT_NUMERIC_FILTER_VALUE
}

export const resolveMetricFilterInfo = (
  metric: MetricAutoAddRuleSource | undefined,
  runRows: RunRowLike[],
): { type: "string" | "number"; uniqueValues: string[] } => {
  if (!metric) {
    return { type: "number", uniqueValues: [] }
  }

  const { metricType, categoryOptions } = getMetricTypeInfo(metric)
  if (metricType === "binary") {
    return { type: "string", uniqueValues: BINARY_FILTER_VALUES }
  }
  if (categoryOptions.length > 0) {
    return { type: "string", uniqueValues: categoryOptions }
  }
  if (metricType === "categorical") {
    return { type: "string", uniqueValues: [] }
  }
  return getMetricFilterInfo(metric, runRows)
}
